import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AlertCircle, Loader2, PackagePlus, Search, X, ShieldCheck, ShoppingCart,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { posApi } from '@/services/api';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';
import { isEuOrUk, isUk, UK_VAT_CATEGORIES } from '@/data/locales';
import { getCurrencySymbol } from '@/utils/currency';
import { canBypassStaffLimits } from '@/utils/staffPermissions';
import ManagerOverrideModal from '@/components/pos/ManagerOverrideModal';
import type { UserPermissions } from '@/types';

export type PublicBarcodeLookup = {
  found: boolean;
  eligible?: boolean;
  barcode: string;
  item_name?: string;
  brand?: string;
  size?: string;
  image_url?: string;
  description?: string;
  ingredients?: string;
  allergens?: string;
  categories?: string;
  category_hints?: string[];
  suggested_tax_rate?: number | null;
  suggested_sale_price?: number | null;
  source?: string | null;
};

export type UnknownBarcodeSavePayload = {
  barcode: string;
  item_name: string;
  brand: string;
  size: string;
  category: string;
  tax_rate: number;
  sale_price: number;
  description?: string;
  image_url?: string;
  lookup_source?: string | null;
  manager_approver?: string;
  persist: boolean;
};

type CategoryRow = { name: string; category_name: string };

/** Brand + name + size for easy recognition on the shelf / next scan. */
function composeProductName(name: string, brand: string, size: string): string {
  let base = (name || '').trim();
  const b = (brand || '').trim();
  const s = (size || '').trim();
  if (b) {
    if (!base) base = b;
    else if (!base.toLowerCase().includes(b.toLowerCase())) base = `${b} ${base}`;
  }
  if (s && base && !base.toLowerCase().includes(s.toLowerCase())) {
    base = `${base} ${s}`;
  }
  return base.trim();
}

/** Map public OFF category hints onto a shop POS Item Category name. */
function matchShopCategory(
  hints: string[] | string | undefined,
  categories: CategoryRow[],
): string {
  if (!categories.length) return '';
  const parts = (Array.isArray(hints) ? hints : String(hints || '').split(/[,;/]/))
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
  if (!parts.length) return '';

  for (const c of categories) {
    const cn = (c.category_name || c.name || '').toLowerCase();
    if (!cn) continue;
    if (parts.some((p) => p === cn || cn.includes(p) || p.includes(cn))) {
      return c.name;
    }
  }

  const aliasGroups: Array<{ needles: string[]; haystack: string[] }> = [
    { needles: ['drink', 'beverage', 'soda', 'soft drink', 'cola', 'juice'], haystack: ['drink', 'beverage', 'soft', 'soda'] },
    { needles: ['snack', 'crisp', 'confection', 'sweet', 'chocolate', 'candy'], haystack: ['snack', 'sweet', 'confection', 'chocolate'] },
    { needles: ['dairy', 'milk', 'yogurt', 'cheese'], haystack: ['dairy', 'milk', 'cheese'] },
    { needles: ['bakery', 'bread', 'cake'], haystack: ['bakery', 'bread'] },
    { needles: ['grocery', 'food', 'grocery'], haystack: ['grocery', 'food', 'general'] },
  ];
  for (const group of aliasGroups) {
    if (!parts.some((p) => group.needles.some((n) => p.includes(n)))) continue;
    const hit = categories.find((c) => {
      const cn = (c.category_name || c.name || '').toLowerCase();
      return group.haystack.some((h) => cn.includes(h));
    });
    if (hit) return hit.name;
  }
  return '';
}

function defaultTaxRate(ukShop: boolean, suggested?: number | null): string {
  if (suggested != null && Number.isFinite(Number(suggested))) {
    return String(Number(suggested));
  }
  // Unknown grocery-like items → Z; non-UK shops keep a sensible standard default
  return ukShop ? '0' : '20';
}

type Props = {
  open: boolean;
  barcode: string;
  country?: string | null;
  role: string;
  permissions?: UserPermissions;
  onClose: () => void;
  onSave: (payload: UnknownBarcodeSavePayload) => Promise<void>;
};

export default function UnknownBarcodeModal({
  open,
  barcode,
  country,
  role,
  permissions,
  onClose,
  onSave,
}: Props) {
  const priceRef = useRef<HTMLInputElement>(null);
  const currency = getCurrencySymbol();
  const ukShop = isUk(country);
  const canCreate = canBypassStaffLimits(role) || !!permissions?.can_create_items;

  const [lookup, setLookup] = useState<PublicBarcodeLookup | null>(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);

  const [itemName, setItemName] = useState('');
  const [brand, setBrand] = useState('');
  const [size, setSize] = useState('');
  const [category, setCategory] = useState('');
  const [salePrice, setSalePrice] = useState('');
  const [taxRate, setTaxRate] = useState('0');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showManager, setShowManager] = useState(false);
  const [pendingPersist, setPendingPersist] = useState(false);

  // Esc closes this popup unless a nested manager override is open or save is in flight.
  useEscapeToClose(
    () => {
      if (saving || showManager) return;
      onClose();
    },
    open,
  );

  const { data: categories = [] } = useQuery({
    queryKey: ['posCategories', 'Product'],
    queryFn: async () => {
      const res = await posApi.getCategories({ category_type: 'Product' });
      return (res.data.message || []) as CategoryRow[];
    },
    enabled: open,
    staleTime: 60_000,
  });

  const resetFromLookup = useCallback((data: PublicBarcodeLookup | null) => {
    setLookup(data);
    const rawName = (data?.item_name || '').trim();
    const rawBrand = (data?.brand || '').trim();
    const rawSize = (data?.size || '').trim();
    setBrand(rawBrand);
    setSize(rawSize);
    setItemName(composeProductName(rawName, rawBrand, rawSize));
    const suggested = data?.suggested_sale_price;
    setSalePrice(suggested != null && Number(suggested) > 0 ? String(suggested) : '');
    setTaxRate(defaultTaxRate(ukShop, data?.suggested_tax_rate));
    setCategory(''); // filled when shop categories are available
    setError('');
    setLookupFailed(false);
    setTimeout(() => priceRef.current?.focus(), 120);
  }, [ukShop]);

  useEffect(() => {
    if (!open || !barcode) return;
    let cancelled = false;
    setLookup(null);
    setLookupLoading(true);
    setLookupFailed(false);
    setItemName('');
    setBrand('');
    setSize('');
    setSalePrice('');
    setError('');

    const run = async () => {
      if (!isEuOrUk(country)) {
        if (!cancelled) {
          resetFromLookup({ found: false, barcode, eligible: false });
          setLookupLoading(false);
        }
        return;
      }
      try {
        const res = await posApi.lookupPublicBarcode(barcode);
        if (cancelled) return;
        const data = (res.data.message || { found: false, barcode }) as PublicBarcodeLookup;
        resetFromLookup(data);
      } catch {
        if (cancelled) return;
        setLookupFailed(true);
        resetFromLookup({ found: false, barcode });
      } finally {
        if (!cancelled) setLookupLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, barcode, country, resetFromLookup]);

  // When shop categories load after lookup, fill the Category select if still empty.
  useEffect(() => {
    if (!open || !lookup?.found || !categories.length || category) return;
    const matched = matchShopCategory(
      lookup.category_hints?.length ? lookup.category_hints : lookup.categories,
      categories,
    );
    if (matched) setCategory(matched);
  }, [open, lookup, categories, category]);

  const displayBarcode = lookup?.barcode || barcode;
  const titleHint = useMemo(() => {
    if (lookupLoading) return 'Looking up product…';
    if (lookup?.found) {
      return 'This barcode is not available in shop products, but we found it in public data';
    }
    if (lookupFailed) return 'Lookup unavailable — enter details';
    return 'Unknown barcode — enter details';
  }, [lookupLoading, lookup?.found, lookupFailed]);

  const buildPayload = (persist: boolean, manager_approver?: string): UnknownBarcodeSavePayload | null => {
    const name = itemName.trim();
    const price = parseFloat(salePrice);
    if (!name) {
      setError('Enter a product name');
      return null;
    }
    if (!Number.isFinite(price) || price <= 0) {
      setError('Enter a selling price');
      priceRef.current?.focus();
      return null;
    }
    return {
      barcode: displayBarcode,
      item_name: name,
      brand: brand.trim(),
      size: size.trim(),
      category: category.trim(),
      tax_rate: parseFloat(taxRate) || 0,
      sale_price: price,
      description: (lookup?.description || '').trim() || undefined,
      image_url: (lookup?.image_url || '').trim() || undefined,
      lookup_source: lookup?.source || (lookup?.found ? 'public' : 'manual'),
      manager_approver,
      persist,
    };
  };

  const submit = async (persist: boolean, manager_approver?: string) => {
    const payload = buildPayload(persist, manager_approver);
    if (!payload) return;
    if (persist && !canCreate && !manager_approver) {
      setPendingPersist(true);
      setShowManager(true);
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(payload);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Could not add product';
      setError(typeof msg === 'string' ? msg : 'Could not add product');
      toast.error(typeof msg === 'string' ? msg : 'Could not add product');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/50 p-0 sm:p-4"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget && !saving && !showManager) onClose();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && !saving && !showManager) {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }
        }}
      >
        <div
          className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
          role="dialog"
          aria-modal="true"
          tabIndex={-1}
        >
          <div className="px-4 py-3 border-b border-gray-100 flex items-start justify-between gap-3 shrink-0">
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <PackagePlus className="w-5 h-5 text-primary-600 shrink-0" />
                New product
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">{titleHint}</p>
              <p className="text-xs font-mono text-gray-400 mt-1 truncate">Barcode {displayBarcode}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full min-h-[44px] min-w-[44px]"
              disabled={saving}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {lookupLoading && (
              <div className="flex items-center gap-3 p-3 rounded-xl bg-primary-50 text-primary-800 text-sm">
                <Loader2 className="w-5 h-5 animate-spin shrink-0" />
                Searching Open Food, Beauty, Pet Food & Products Facts…
              </div>
            )}

            {!lookupLoading && lookupFailed && (
              <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 text-amber-900 text-sm">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                Online lookup failed. You can still type the details and continue the sale.
              </div>
            )}

            {!lookupLoading && lookup?.found && (
              <div className="space-y-2">
                <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-100 text-amber-900 text-sm">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                  <p>
                    This barcode is not available in shop products, but we found it in public data.
                    Set a price and save to add it.
                  </p>
                </div>
                <div className="flex gap-3 p-3 rounded-xl border border-emerald-100 bg-emerald-50/40">
                  {lookup.image_url ? (
                    <img
                      src={lookup.image_url}
                      alt=""
                      className="w-16 h-16 object-contain rounded-lg bg-white border border-emerald-100 shrink-0"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-white border border-emerald-100 flex items-center justify-center shrink-0">
                      <Search className="w-6 h-6 text-emerald-400" />
                    </div>
                  )}
                  <div className="min-w-0 text-sm">
                    <p className="font-semibold text-gray-900 leading-snug">
                      {composeProductName(lookup.item_name || '', lookup.brand || '', lookup.size || '')
                        || lookup.item_name
                        || 'Matched product'}
                    </p>
                    {(lookup.brand || lookup.size) && (
                      <p className="text-xs text-gray-600 mt-1">
                        {[lookup.brand, lookup.size].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {lookup.ingredients && (
                      <p className="text-xs text-gray-600 mt-1.5 line-clamp-2">
                        <span className="font-medium text-gray-700">Ingredients:</span> {lookup.ingredients}
                      </p>
                    )}
                    {lookup.allergens && (
                      <p className="text-xs text-amber-800 mt-1">
                        <span className="font-medium">Allergens:</span> {lookup.allergens}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Product name</label>
              <input
                className="input-field w-full min-h-[48px] text-base"
                value={itemName}
                onChange={(e) => setItemName(e.target.value)}
                placeholder="Product name"
                disabled={saving}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Brand</label>
                <input
                  className="input-field w-full min-h-[44px]"
                  value={brand}
                  onChange={(e) => setBrand(e.target.value)}
                  placeholder="Optional"
                  disabled={saving}
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Size</label>
                <input
                  className="input-field w-full min-h-[44px]"
                  value={size}
                  onChange={(e) => setSize(e.target.value)}
                  placeholder="e.g. 500ml"
                  disabled={saving}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">Category</label>
              <select
                className="input-field w-full min-h-[44px]"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                disabled={saving}
              >
                <option value="">No category</option>
                {categories.map((c) => (
                  <option key={c.name} value={c.name}>
                    {c.category_name || c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">
                {ukShop ? 'VAT' : 'Tax %'}
              </label>
              {ukShop ? (
                <select
                  className="input-field w-full min-h-[44px]"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={saving}
                >
                  {UK_VAT_CATEGORIES.map((c) => (
                    <option key={c.code} value={String(c.rate)}>
                      {c.label}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  className="input-field w-full min-h-[44px]"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  disabled={saving}
                />
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-800 mb-1.5">Selling price *</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                  {currency}
                </span>
                <input
                  ref={priceRef}
                  type="text"
                  inputMode="decimal"
                  className="input-field pl-8 w-full min-h-[56px] text-xl font-bold"
                  value={salePrice}
                  onChange={(e) => setSalePrice(e.target.value)}
                  placeholder="0.00"
                  disabled={saving}
                />
              </div>
              {lookup?.suggested_sale_price != null && Number(lookup.suggested_sale_price) > 0 && (
                <button
                  type="button"
                  className="mt-1.5 text-xs font-semibold text-primary-600"
                  onClick={() => setSalePrice(String(lookup.suggested_sale_price))}
                >
                  Use suggested {currency}{Number(lookup.suggested_sale_price).toFixed(2)}
                </button>
              )}
            </div>

            {error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl p-3">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </div>
            )}

            {!canCreate && (
              <p className="text-xs text-amber-700 flex items-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Saving to products list needs manager approval on your account.
              </p>
            )}
          </div>

          <div className="shrink-0 border-t border-gray-100 p-3 space-y-2 bg-white">
            <button
              type="button"
              disabled={saving || lookupLoading}
              onClick={() => submit(true)}
              className="btn-primary w-full min-h-[52px] text-base font-bold flex items-center justify-center gap-2"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackagePlus className="w-5 h-5" />}
              Save to Products and add to invoice
            </button>
            <button
              type="button"
              disabled={saving || lookupLoading}
              onClick={() => submit(false)}
              className="w-full min-h-[48px] text-sm font-semibold rounded-xl border-2 border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <ShoppingCart className="w-4 h-4" />
              Add to this invoice only
            </button>
          </div>
        </div>
      </div>

      <ManagerOverrideModal
        open={showManager}
        onClose={() => {
          setShowManager(false);
          setPendingPersist(false);
        }}
        title="Manager approval"
        message="Approve creating this product in the catalog."
        onApproved={(approverId) => {
          setShowManager(false);
          if (pendingPersist) {
            setPendingPersist(false);
            void submit(true, approverId);
          }
        }}
      />
    </>
  );
}
